/**
 * The case header: back to the queue, the subject, "3 of 12" with Previous / Next
 * (keys `j` / `k`, registered with the action bar), and the help button. Nothing else:
 * the customer, the lines and the status are the work column's.
 */

import { ArrowLeft, ArrowRight, Icon } from "@norican/icons";
import { Button, PageHeader, Typography } from "@norican/react";
import { Link } from "react-router";

import { DocumentTitle } from "@/components/document-title";
import { type CaseFlow } from "@/features/case-review/use-case-flow";
import { HelpButton } from "@/features/help";
import { queueHref } from "@/features/queue/queue-defaults";
import { FEATURES } from "@/lib/features";
import { type QuoteCaseDetail } from "@/lib/mock";
import { formatDayTime } from "@/lib/work-labels";

export interface CaseHeaderProps {
  detail: QuoteCaseDetail;
  /** The walk through the queue; null on a shared link. */
  flow: CaseFlow | null;
}

/** "3 of 12" in the queue; null without an order. */
export function positionText(flow: CaseFlow | null): string | null {
  if (!flow?.fromQueue || flow.position == null) {
    return null;
  }
  return `${flow.position} of ${flow.total ?? "?"}`;
}

export function CaseHeader({ detail, flow }: CaseHeaderProps) {
  const title = detail.subject?.trim() || detail.ref;
  const walking = FEATURES.caseFlow && flow?.fromQueue === true;
  const received = formatDayTime(detail.receivedAt);
  return (
    <>
      <DocumentTitle title={title} />
      <PageHeader
        title={title}
        breadcrumbs={
          <Link
            to={queueHref()}
            className="text-accent-fg-subtle inline-flex items-center gap-1 text-xs font-semibold hover:underline"
          >
            <Icon icon={ArrowLeft} size="xs" />
            Queue
          </Link>
        }
        subtitle={
          <span className="text-fg-subtle text-xs">
            {[`Case ${detail.ref}`, received ? `received ${received}` : ""]
              .filter(Boolean)
              .join(" · ")}
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {walking && positionText(flow) && (
              <Typography as="span" variant="caption" tone="subtle" className="tabular-nums">
                {positionText(flow)}
              </Typography>
            )}
            {walking && (
              <Button
                size="sm"
                variant="ghost"
                tone="neutral"
                iconOnly
                disabled={!flow?.previous}
                aria-label="Previous case"
                title="Previous case (k)"
                startIcon={<Icon icon={ArrowLeft} size="xs" />}
                onClick={() => flow?.goPrevious()}
              />
            )}
            {walking && (
              <Button
                size="sm"
                variant="ghost"
                tone="neutral"
                iconOnly
                loading={flow?.loadingNext}
                aria-label="Next case"
                title="Next case (j)"
                startIcon={<Icon icon={ArrowRight} size="xs" />}
                onClick={() => flow?.goNext()}
              />
            )}
            <HelpButton topic="case-page" />
          </div>
        }
      />
    </>
  );
}
